import { CloseButton, CrudMode, DropDownMenu, ListContext, ListContextValue, MenuItemType, Stack, Typography, newToast } from "@compass/core";
import { DataModelInformation, SubmitButton } from "components";
import {
  ApplicationResponseFile,
  DataModelEnum,
  FileTypeCategory,
  Permission,
  PermissionAction,
  StudentFile,
  StudentServiceFile,
  UploadFile,
} from "generated/openApi";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { FileDetailTypeEnum } from "./FileDetailTypeEnum";
import { useContext } from "react";
import { isAuthorized } from "services";

export const FileDetailHeader = (props: {
  crudMode: CrudMode;
  entityId: Number;
  // parentEntityId: Number;
  fileType: FileTypeCategory;
  fetch?: (id: number) => void;
  reroute?: (id: number, crudMode: CrudMode) => void;
  applicationResponseFile: ApplicationResponseFile | undefined;
  studentFile: StudentFile | undefined;
  studentServiceFile: StudentServiceFile | undefined;
  onSave: (id: number) => void;
  onFileChange?: (uploadFile: StudentServiceFile | StudentFile | ApplicationResponseFile) => void;
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const listContext = useContext<ListContextValue>(ListContext);

  const save = () => {
    // There's probably a better way to do this, but right now if onFileChange exists, they want to create files (child entities) at the same time as the parent entities (think Service + Service Files)
    // So if onFilechange() exists, use the workflow to let it be a controlled component, if not, let the API do the work
    if (props.onFileChange) {
      if (props.fileType == FileTypeCategory.Application && props.applicationResponseFile) {
        props.onFileChange(props.applicationResponseFile);
      }
      else if (props.fileType == FileTypeCategory.StudentServiceFile && props.studentServiceFile) {
        props.onFileChange(props.studentServiceFile);
      }
      else if (props.fileType == FileTypeCategory.StudentFile && props.studentFile) {
        props.onFileChange(props.studentFile);
      }
    }
    else {
      // If onFileChange does not exist.. let the API do the work (ie the parent entity already exists)
      if (props.entityId == 0) {
        if (props.fileType == FileTypeCategory.Application && props.applicationResponseFile) {
          ApiService.studentApplications.saveCreateFile(props.applicationResponseFile)
            .then((applicationResponseFileId) => {
              // props.reroute(applicationResponseFileId, "view");
              // props.fetch(applicationResponseFileId);
              listContext.loadList?.();
              dispatch(setAppToast(newToast("success")));
            })
            .catch((ex) => {
              dispatch(setAppToast(newToast("error")));
            });
        }
        else if (props.fileType == FileTypeCategory.StudentServiceFile && props.studentServiceFile) {
          ApiService.studentServices.saveCreateStudentFile(props.studentServiceFile)
            .then((studentServiceFileId) => {
              // props.reroute(studentServiceFileId, "view");
              // props.fetch(studentServiceFileId);
              listContext.loadList?.();
              dispatch(setAppToast(newToast("success")));
            })
            .catch((ex) => {
              dispatch(setAppToast(newToast("error")));
            });
        }
        else if (props.fileType == FileTypeCategory.StudentFile && props.studentFile) {
          ApiService.studentFiles.saveCreate(props.studentFile)
            .then((studentFileId) => {
              // props.reroute(studentFileId, "view");
              // props.fetch(studentFileId);
              listContext.loadList?.();
              dispatch(setAppToast(newToast("success")));
            })
            .catch((ex) => {
              dispatch(setAppToast(newToast("error")));
            });
        }
      }
      else {
        if (props.fileType == FileTypeCategory.Application && props.applicationResponseFile) {
          ApiService.studentApplications.saveCreateFile(props.applicationResponseFile)
            .then((applicationResponseFileId) => {
              // props.reroute(applicationResponseFileId, "edit");
              // props.fetch(applicationResponseFileId);
              listContext.loadList?.();
              dispatch(setAppToast(newToast("success")));
            })
            .catch((ex) => {
              dispatch(setAppToast(newToast("error")));
            });
        }
        else if (props.fileType == FileTypeCategory.StudentServiceFile && props.studentServiceFile) {
          ApiService.studentServices.saveUpdateStudentFile(props.studentServiceFile)
            .then((studentServiceFileId) => {
              // props.reroute(studentServiceFileId, "edit");
              // props.fetch(studentServiceFileId);
              listContext.loadList?.();
              dispatch(setAppToast(newToast("success")));
            })
            .catch((ex) => {
              dispatch(setAppToast(newToast("error")));
            });
        }
        else if (props.fileType == FileTypeCategory.StudentFile && props.studentFile) {
          ApiService.studentFiles.saveUpdate(props.studentFile)
            .then((studentFileId) => {
              // props.reroute(studentFileId, "edit");
              // props.fetch(studentFileId);
              listContext.loadList?.();
              dispatch(setAppToast(newToast("success")));
            })
            .catch((ex) => {
              dispatch(setAppToast(newToast("error")));
            });
        }
      }
    }

  };

  const postStudentFileDelete = async (studentFileId: number) => {
    try {
      const response = await ApiService.studentFiles.delete(studentFileId);
      navigate("../");
      listContext.loadList?.();
      dispatch(setAppToast(newToast("success")));
    }
    catch {
      dispatch(setAppToast(newToast("error")));
    }
  }

  const postStudentServiceFileDelete = async (studentServiceFileId: number) => {
    try {
      const response = await ApiService.studentFiles.deleteStudentServiceFile(studentServiceFileId);
      navigate("../");
      listContext.loadList?.();
      dispatch(setAppToast(newToast("success")));
    }
    catch {
      dispatch(setAppToast(newToast("error")));
    }
  }

  const menuItems: MenuItemType[] = [];

  if (props.studentFile && props.studentFile?.id > 0) {
    if (isAuthorized(Permission.StudentFile, PermissionAction.Delete))
      menuItems.push({
        label: <Typography color="error">Delete</Typography>,
        action: () => postStudentFileDelete(props.studentFile!.id),
      });
  }
  else if (props.studentServiceFile && props.studentServiceFile?.id > 0) {
    if (isAuthorized(Permission.StudentService, PermissionAction.Delete))
      menuItems.push({
        label: <Typography color="error">Delete</Typography>,
        action: () => postStudentServiceFileDelete(props.studentServiceFile!.id),
      });
  }

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing="sm">
        Upload File
        {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}
      </Stack>

      <Stack direction="row" alignItems="center" spacing="sm">
        {props.studentFile && props.studentFile?.id > 0 && <DataModelInformation color="info" dataModelType={DataModelEnum.StudentFile} dataModelId={props.studentFile?.id} />}

        <CloseButton theme="Dark" />

        <SubmitButton
          theme="Dark"
          onClick={() => save()}
          disabled={(props.crudMode != "edit")}
        />
      </Stack>
    </Stack>
  );
};
